import React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../../../components/Navbar';
import PageWithAsyncData from '../../../components/PageWithAsyncData';
import {
  FullScreenLayout,
  TopBar,
  PageTitle,
  TableWrapper,
  EmptyStateText,
} from '../../../components/Admin/AdminLayout';
import { ROUTES } from '../../../routing';
import { useCmsGetModules } from '../../../hooks/use-api/admin';
import { createColumnHelper } from '@tanstack/react-table';
import Table from '../../../components/Table/CompactTable';
import type { CmsModuleListItem } from '../../../api/admin';
import {
  FiltersHorizontalWrapper,
  TextFilterPill,
  SingleSelectFilterPill,
} from '../../../components/elements/Filter';
import day from 'dayjs';
import CMSPageWrapper, {
  SmallButton,
} from '../../../components/Admin/CMSPageWrapper';
import { CMS_SIDEBAR_PAGES } from '../../../components/Admin/CMSSideBar';

const SecondaryText = styled.span`
  color: ${({ theme }) => theme.colors.textRegular};
`;

const StatusBadge = styled.span<{ isLive: boolean }>`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background: ${({ isLive, theme }) =>
    isLive ? theme.colors.textSuccess + '20' : theme.colors.textRegular + '20'};
  color: ${({ isLive, theme }) =>
    isLive ? theme.colors.textSuccess : theme.colors.textRegular};
`;

const columnHelper = createColumnHelper<CmsModuleListItem>();

export default function ModulesList() {
  const { data: modules, isLoading, error, refetch } = useCmsGetModules();
  const history = useHistory();

  const [titleFilter, setTitleFilter] = React.useState('');
  const [statusFilter, setStatusFilter] =
    React.useState<'ALL' | 'LIVE' | 'ARCHIVED'>('ALL');

  const filteredModules = React.useMemo(() => {
    let filtered = modules ?? [];
    const trimmed = titleFilter.trim().toLowerCase();
    if (trimmed) {
      filtered = filtered.filter((m) =>
        m.title.toLowerCase().includes(trimmed)
      );
    }
    if (statusFilter !== 'ALL') {
      filtered = filtered.filter((m) => m.publishStatus === statusFilter);
    }
    return filtered;
  }, [modules, titleFilter, statusFilter]);

  const columns = React.useMemo(
    () => [
      columnHelper.accessor('title', {
        header: 'Title',
        cell: ({ getValue }) => (
          <span style={{ fontWeight: 500 }}>{getValue()}</span>
        ),
      }),
      columnHelper.accessor('slug', {
        header: 'Slug',
        cell: ({ getValue }) => <SecondaryText>{getValue()}</SecondaryText>,
      }),
      columnHelper.accessor('topic', {
        header: 'Topic',
        cell: ({ getValue }) => <SecondaryText>{getValue()}</SecondaryText>,
      }),
      columnHelper.accessor('activitiesCount', {
        header: 'Num. Activities',
        cell: ({ getValue }) => <SecondaryText>{getValue()}</SecondaryText>,
      }),
      columnHelper.accessor('publishStatus', {
        header: 'Status',
        cell: ({ getValue }) => {
          const isLive = getValue() === 'LIVE';
          return (
            <StatusBadge isLive={isLive}>
              {isLive ? 'Live' : 'Archived'}
            </StatusBadge>
          );
        },
      }),
      columnHelper.accessor((row) => new Date(row.updatedAt).getTime(), {
        id: 'updatedAt',
        header: 'Last Updated',
        cell: ({ row }) => (
          <SecondaryText>
            {day(row.original.updatedAt).format('DD/MM/YYYY HH:mm')}
          </SecondaryText>
        ),
      }),
    ],
    []
  );

  return (
    <PageWithAsyncData
      isLoading={isLoading}
      error={error}
      retry={refetch}
      noScroll
    >
      <Navbar
        breadCrumbs={[
          { label: 'Admin Dashboard', url: ROUTES.ADMIN.HOME.getPath() },
          { label: 'Modules', url: null },
        ]}
      />
      <CMSPageWrapper
        currentPage={CMS_SIDEBAR_PAGES.MODULES}
        shouldAddEmptyDiv={false}
      >
        <FullScreenLayout>
          <PageTitle>Modules</PageTitle>
          <TopBar>
            <FiltersHorizontalWrapper style={{ marginBottom: 0, flex: 1 }}>
              <TextFilterPill
                label="Title"
                value={titleFilter}
                placeholder="Filter by title..."
                onChange={setTitleFilter}
              />
              <SingleSelectFilterPill
                label="Status"
                value={statusFilter}
                options={[
                  { label: 'All', value: 'ALL' },
                  { label: 'Live', value: 'LIVE' },
                  { label: 'Archived', value: 'ARCHIVED' },
                ]}
                onChange={(v) =>
                  setStatusFilter(v as 'ALL' | 'LIVE' | 'ARCHIVED')
                }
                shouldHideClear
              />
            </FiltersHorizontalWrapper>
            <SmallButton
              label="Create New"
              type="primary"
              faIcon="fa-solid fa-plus"
              showIconBeforeLabel
              onClick={() =>
                history.push(ROUTES.ADMIN.CMS_MODULE_CREATE.getPath())
              }
            />
          </TopBar>
          {filteredModules.length === 0 ? (
            <EmptyStateText>
              {titleFilter
                ? 'No modules match the current filter.'
                : 'No modules yet.'}
            </EmptyStateText>
          ) : (
            <TableWrapper>
              <Table
                data={filteredModules}
                getRowId={(row) => row.slug}
                columns={columns}
                className="modules-table"
                getRowProps={(row) => ({
                  onClick: () =>
                    history.push(
                      ROUTES.ADMIN.CMS_MODULE_EDIT.getPath({
                        slug: row.original.slug,
                      })
                    ),
                })}
              />
            </TableWrapper>
          )}
        </FullScreenLayout>
      </CMSPageWrapper>
    </PageWithAsyncData>
  );
}
